import { ArrowRight, BarChart3, FileText, FolderKanban, LogOut, Settings2, ShieldCheck, Sparkles, Target, UserRound, Workflow } from "lucide-react";
import { useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../../lib/api";
import { buildRouteContextLine, getRouteOwnershipContext } from "../../lib/routeOwnershipRegistry";

export const TRUEVANCE_BRAND_NAME = "Truevance Connect Business Center";
export const TRUEVANCE_BRAND_SHORT = "Truevance Connect";
export const TRUEVANCE_LOGO_SRC = "/assets/truevance/truevance-logo.png";
export const TRUEVANCE_FAVICON_SRC = "/assets/truevance/truevance-symbol.ico";
const TRUEVANCE_THEME_COLOR = "#071A2F";

const truevanceNavLinks = [
  { label: TRUEVANCE_BRAND_NAME, to: "/admin/truevance-business-center", icon: Sparkles },
  { label: "Sales & Lead Center", to: "/admin/truevance-sales-center", icon: Target },
  { label: "Assessment Center", to: "/admin/truevance-assessment-center", icon: ShieldCheck },
  { label: "Proposal Center", to: "/admin/truevance-proposal-center", icon: FileText },
  { label: "Client Center", to: "/admin/truevance-client-center", icon: UserRound },
  { label: "Project Center", to: "/admin/truevance-project-center", icon: Workflow },
  { label: "Tasks Center", to: "/admin/truevance-tasks", icon: FolderKanban },
  { label: "Document Center", to: "/admin/truevance-document-center", icon: FileText },
  { label: "Roles & Permissions", to: "/admin/truevance-roles-center", icon: ShieldCheck },
  { label: "Communication Center", to: "/admin/truevance-communication-center", icon: ArrowRight },
  { label: "Analytics Center", to: "/admin/truevance-analytics-center", icon: BarChart3 },
  { label: "Settings Center", to: "/admin/truevance-settings-center", icon: Settings2 },
];

export function useTruevanceBranding(title: string, description: string) {
  useEffect(() => {
    const normalizedTitle = title.trim();
    const fullTitle =
      normalizedTitle === TRUEVANCE_BRAND_NAME || normalizedTitle.endsWith(` | ${TRUEVANCE_BRAND_NAME}`)
        ? normalizedTitle
        : `${normalizedTitle} | ${TRUEVANCE_BRAND_NAME}`;
    document.title = fullTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);

    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousThemeColor = themeMeta?.getAttribute("content") ?? "";
    if (themeMeta) themeMeta.setAttribute("content", TRUEVANCE_THEME_COLOR);

    const iconSelectors = ['link[rel="icon"]', 'link[rel="shortcut icon"]'] as const;
    const iconLinks = iconSelectors
      .map((selector) => document.querySelector<HTMLLinkElement>(selector))
      .filter((link): link is HTMLLinkElement => Boolean(link));
    const previousIconHrefs = iconLinks.map((link) => link.getAttribute("href"));
    iconLinks.forEach((link) => {
      link.setAttribute("href", TRUEVANCE_FAVICON_SRC);
      link.setAttribute("type", "image/x-icon");
    });

    return () => {
      if (themeMeta) {
        if (previousThemeColor) themeMeta.setAttribute("content", previousThemeColor);
      }
      iconLinks.forEach((link, index) => {
        const previousHref = previousIconHrefs[index];
        if (previousHref) link.setAttribute("href", previousHref);
      });
    };
  }, [description, title]);
}

export function TruevanceBrandHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const routeContext = getRouteOwnershipContext(location.pathname);
  const routeContextLine = buildRouteContextLine(location.pathname);

  async function handleLogout() {
    try {
      await logout();
    } finally {
      localStorage.removeItem("ktd_demo_auth");
      navigate("/admin/login", { replace: true });
    }
  }

  return (
    <header className="truevance-shell truevance-header sticky top-0 z-40 border-b border-[rgba(34,224,208,.18)] px-4 py-4 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <Link to={routeContext.returnRoute} className="focus-ring inline-flex max-w-full items-center gap-4 rounded-2xl">
            <img
              src={TRUEVANCE_LOGO_SRC}
              alt="Truevance Connect Business Center"
              className="block h-12 w-12 shrink-0 rounded-xl object-cover shadow-[0_18px_45px_rgba(0,194,184,.18)] sm:h-14 sm:w-14"
              draggable={false}
            />
            <span className="leading-tight">
              <span className="block text-xs font-black uppercase tracking-[0.22em] text-[#22E0D0]">{TRUEVANCE_BRAND_NAME}</span>
              <span className="block font-display text-2xl font-bold text-[#F5F7FA] sm:text-[2rem]">{TRUEVANCE_BRAND_NAME}</span>
              <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#C7D2DE]">Private consulting operations command center</span>
              <span className="mt-1 block text-[10px] font-black uppercase tracking-[0.18em] text-[#C7D2DE]/90">{routeContextLine}</span>
            </span>
          </Link>

          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={routeContext.returnRoute}
              className="truevance-pill focus-ring inline-flex min-h-11 items-center justify-center rounded-full px-4 text-sm font-extrabold"
            >
              Workspace Home
            </Link>
            <button
              type="button"
              onClick={() => void handleLogout()}
              className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[rgba(34,224,208,.28)] bg-[rgba(255,255,255,.04)] px-4 text-sm font-extrabold text-[#F5F7FA] transition hover:border-[rgba(34,224,208,.5)] hover:bg-[rgba(34,224,208,.1)]"
            >
              <LogOut className="size-4" aria-hidden="true" />
              Logout
            </button>
          </div>
        </div>

        <nav className="luxury-scrollbar flex gap-2 overflow-x-auto pb-1" aria-label="Truevance Connect Business Center navigation">
          {truevanceNavLinks.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `focus-ring inline-flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-4 text-sm font-bold transition ${
                    isActive
                      ? "border-[rgba(34,224,208,.55)] bg-[rgba(34,224,208,.16)] text-[#F5F7FA] shadow-[0_16px_32px_rgba(0,194,184,.16)]"
                      : "border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.03)] text-[#E8EEF4] hover:border-[rgba(34,224,208,.42)] hover:bg-[rgba(255,255,255,.06)]"
                  }`
                }
              >
                <Icon className="size-4" aria-hidden="true" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </header>
  );
}
